/* Install only in the reviewed copied theme together with native order matching. */
(() => {
  "use strict";
  const appOrigin = {
    "ascending-pets-app-staging.myshopify.com":
      "https://ascending-pets-staging.vercel.app",
    "shop.ascendingpets.com": "https://app.ascendingpets.com",
  }[location.hostname];
  if (!appOrigin) return;
  const storageKey = "ap-native-shopping-v1";
  const pattern = /^apn1\.[a-f0-9-]{36}\.[A-Za-z0-9_-]{43}$/;
  const incoming = new URLSearchParams(location.hash.slice(1)).get(
    "ap_shopping",
  );
  let reference = null;
  try {
    if (incoming) {
      history.replaceState(null, "", location.pathname + location.search);
      sessionStorage.removeItem(storageKey);
      if (pattern.test(incoming)) sessionStorage.setItem(storageKey, incoming);
    }
    const saved = sessionStorage.getItem(storageKey);
    if (saved && pattern.test(saved)) reference = saved;
  } catch {
    reference = null;
  }
  const notice = document.createElement("div");
  notice.setAttribute("role", "status");
  notice.style.cssText =
    "padding:12px 20px;background:#f3f1e7;color:#344333;font:17px/1.5 Faustina,Georgia,serif";
  notice.textContent = reference
    ? "Connecting your pet’s keepsakes…"
    : "For your pet’s keepsakes, open this store from their private Care Page first.";
  document.body.prepend(notice);
  const originalFetch = window.fetch.bind(window);
  let petName = null,
    sessionPromise = null,
    privateEpoch = 0;
  async function ensureSession(force = false) {
    if (petName && !force) return petName;
    if (!reference) throw new Error("Missing shopping session.");
    if (!sessionPromise) {
      const epoch = privateEpoch;
      sessionPromise = (async () => {
        const response = await originalFetch(
          appOrigin + "/api/store/memorial-cart",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            cache: "no-store",
            credentials:
              location.hostname === "shop.ascendingpets.com"
                ? "include"
                : "omit",
            body: JSON.stringify({ action: "session", reference }),
            signal: AbortSignal.timeout(15000),
          },
        );
        if (!response.ok) throw new Error("Shopping session unavailable.");
        const result = await response.json();
        if (
          result.ok !== true ||
          typeof result.petName !== "string" ||
          !result.petName.trim() ||
          result.petName.length > 200
        )
          throw new Error("Shopping session unavailable.");
        if (epoch !== privateEpoch)
          throw new Error("Shopping session changed.");
        await window.apNativeCartHistory(
          result,
          reference,
          originalFetch,
          () => epoch === privateEpoch,
        );
        if (epoch !== privateEpoch)
          throw new Error("Shopping session changed.");
        sessionPromise = null;
        const wasCleared = !petName;
        petName = result.petName;
        if (wasCleared) notice.textContent = "Keepsakes for " + petName;
        prepareForms();
        return petName;
      })().catch((error) => {
        if (epoch === privateEpoch) {
          clearPrivate();
          notice.textContent =
            "Please reopen the shop from your pet’s private Care Page before adding items or paying.";
        }
        throw error;
      });
    }
    return sessionPromise;
  }
  function clearPrivate() {
    privateEpoch++;
    sessionPromise = null;
    petName = null;
    window.apClearPurchaseHistory();
    notice.textContent = "Open your private Care Page to continue shopping.";
    prepareForms();
  }
  window.addEventListener("blur", clearPrivate);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) clearPrivate();
    else if (reference) void ensureSession(true).catch(() => {});
  });
  window.addEventListener("focus", () => {
    if (reference) void ensureSession(true).catch(() => {});
  });
  // Do not retain a previous family’s details in an open shared-device store tab.
  setInterval(() => {
    if (reference && !document.hidden) void ensureSession(true).catch(() => {});
  }, 5000);
  function prepareForms() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (new URL(form.action, location.origin).origin !== location.origin)
        return;
      let input = form.querySelector('input[name="properties[_ap_shopping]"]');
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = "properties[_ap_shopping]";
        form.append(input);
      }
      input.value = reference ?? "";
      let pet = form.querySelector('input[name="properties[Pet]"]');
      if (!pet) {
        pet = document.createElement("input");
        pet.type = "hidden";
        pet.name = "properties[Pet]";
        form.append(pet);
      }
      pet.value = petName ?? "";
    });
    // Express buttons skip the cart identity check; payment methods remain in Shopify checkout.
    document
      .querySelectorAll(".shopify-payment-button, ap-case-gate")
      .forEach((e) => {
        e.hidden = true;
      });
  }
  prepareForms();
  if (reference) void ensureSession().catch(() => {});
  new MutationObserver(prepareForms).observe(document.body, {
    childList: true,
    subtree: true,
  });
  window.fetch = async function (input, options) {
    const url = new URL(
      typeof input === "string" || input instanceof URL ? input : input.url,
      location.origin,
    );
    const method = (
      options?.method ?? (input instanceof Request ? input.method : "GET")
    ).toUpperCase();
    if (
      url.origin !== location.origin ||
      !/^\/cart\/add(?:\.js)?$/.test(url.pathname) ||
      method !== "POST"
    )
      return originalFetch(input, options);
    if (!reference)
      throw new Error("Please open the store from your pet’s Care Page first.");
    await ensureSession(true);
    const body =
      options?.body ??
      (input instanceof Request
        ? await (input.headers
            .get("Content-Type")
            ?.startsWith("multipart/form-data")
            ? input.clone().formData()
            : input.clone().text())
        : null);
    const headers = new Headers(
      options?.headers ??
        (input instanceof Request ? input.headers : undefined),
    );
    let fields;
    if (body instanceof FormData || body instanceof URLSearchParams)
      fields = body;
    else if (typeof body === "string" && !body.trimStart().startsWith("{"))
      fields = new URLSearchParams(body);
    if (fields) {
      if (
        !fields.get("id") ||
        [...fields.keys()].some((k) => k.startsWith("items["))
      )
        throw new Error("Unsupported cart addition.");
      if (!window.apConfirmRepeat([{ id: fields.get("id") }]))
        throw new Error("Additional item not confirmed.");
      const prior = fields.get("properties[_ap_shopping]");
      if (prior && prior !== reference)
        throw new Error("Different shopping session.");
      fields.set("properties[_ap_shopping]", reference);
      if (
        fields.get("properties[Pet]") &&
        fields.get("properties[Pet]") !== petName
      )
        throw new Error("Different pet.");
      fields.set("properties[Pet]", petName);
      if (fields instanceof FormData) headers.delete("Content-Type");
      else
        headers.set(
          "Content-Type",
          "application/x-www-form-urlencoded;charset=UTF-8",
        );
      return originalFetch(input, {
        ...options,
        method: "POST",
        headers,
        body: fields,
      });
    }
    if (typeof body !== "string") throw new Error("Unsupported cart addition.");
    const payload = JSON.parse(body);
    const items = Array.isArray(payload.items) ? payload.items : [payload];
    if (!window.apConfirmRepeat(items))
      throw new Error("Additional item not confirmed.");
    if (!items.length || items.length > 8)
      throw new Error("Invalid cart addition.");
    for (const line of items) {
      if (
        !line?.id ||
        (line.properties?._ap_shopping &&
          line.properties._ap_shopping !== reference)
      )
        throw new Error("Different shopping session.");
      line.properties = { ...line.properties, _ap_shopping: reference };
      if (line.properties.Pet && line.properties.Pet !== petName)
        throw new Error("Different pet.");
      line.properties.Pet = petName;
    }
    headers.set("Content-Type", "application/json");
    return originalFetch(input, {
      ...options,
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
  };
  let busy = false;
  async function checkout() {
    if (busy) return;
    busy = true;
    notice.textContent = "Checking your cart before opening Shopify checkout…";
    try {
      if (!reference) throw new Error("Missing shopping session.");
      await ensureSession(true);
      const response = await originalFetch("/cart.js", { cache: "no-store" });
      if (!response.ok) throw new Error("Cart unavailable.");
      const cart = await response.json();
      if (
        !Array.isArray(cart.items) ||
        !cart.items.length ||
        cart.items.length > 8 ||
        cart.items.some((line) => line.properties?._ap_shopping !== reference)
      )
        throw new Error("Mixed shopping sessions.");
      const body = JSON.stringify({
        action: "checkout",
        reference,
        cart: {
          items: cart.items.map((line) => ({
            variant_id: String(line.variant_id),
            quantity: line.quantity,
            properties: line.properties,
          })),
        },
      });
      if (body.length > 16000) throw new Error("Cart too large.");
      const checked = await originalFetch(
        appOrigin + "/api/store/memorial-cart",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials:
            location.hostname === "shop.ascendingpets.com" ? "include" : "omit",
          cache: "no-store",
          body,
          signal: AbortSignal.timeout(15000),
        },
      );
      if (!checked.ok || (await checked.json()).ok !== true)
        throw new Error("Cart could not be verified.");
      // The new cart contains only this checked snapshot. Other tabs cannot mutate it.
      location.assign(await window.apNativeCheckout(cart, originalFetch));
    } catch {
      notice.textContent =
        "We couldn’t confirm this cart. Remove the old or other pet’s items from your cart, then reopen the shop from your pet’s Care Page and add your items again. Questions? Text 385-338-6166.";
      busy = false;
    }
  }
  const pendingForms = new WeakSet();
  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const target = new URL(form.action, location.origin);
      if (target.origin !== location.origin) return;
      if (/^\/cart\/add(?:\.js)?$/.test(target.pathname)) {
        if (!reference) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
        if (reference && !petName) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (!pendingForms.has(form)) {
            pendingForms.add(form);
            void ensureSession()
              .then(() => {
                pendingForms.delete(form);
                form.requestSubmit(event.submitter);
              })
              .catch(() => pendingForms.delete(form));
          }
          return;
        }
        if (!window.apConfirmRepeat([{ id: new FormData(form).get("id") }])) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        prepareForms();
        return;
      }
      if (
        form.action.includes("/cart") &&
        event.submitter?.name === "checkout"
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        void checkout();
      }
    },
    true,
  );
  document.addEventListener(
    "click",
    (event) => {
      const anchor =
        event.target instanceof Element
          ? event.target.closest("a[href]")
          : null;
      if (
        anchor &&
        new URL(anchor.href, location.origin).pathname === "/checkout"
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        void checkout();
      }
    },
    true,
  );
})();
